/**
 * Shared Tailwind class strings for page-level cards and content containers.
 * Use these so shadows and borders stay consistent across list pages, detail pages, and layouts.
 *
 * Surface hierarchy:
 * - pageCard / detailPageCard — outer page shells (soft border, shadow-sm)
 * - innerCard — nested content cards (InfoCard, DetailCard, dashboard widgets)
 */

/** Main content card: white background, rounded, light shadow and border. */
export const pageCard =
  'bg-white dark:bg-gray-800 rounded-lg shadow-sm shadow-gray-200/60 dark:shadow-gray-900/50 border border-gray-200 dark:border-gray-700';

/** Inner content card nested inside pageCard tab panels. Lighter border than page shells. */
export const innerCard =
  'rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm shadow-gray-200/40 dark:shadow-gray-900/40';

/** Page header card (title + icon). Combine with getOuterBorderClasses(entity) for accent. */
export const pageCardHeader =
  'bg-white dark:bg-gray-800 rounded-lg px-3 sm:px-4 py-2.5 sm:py-3 shadow-sm shadow-gray-200/60 dark:shadow-gray-900/50 border border-gray-200 dark:border-gray-700';

/** Header icon wrapper: subtle lift. */
export const pageCardHeaderIcon = 'shadow-sm shadow-gray-200/40 dark:shadow-black/20';

/** List/Calendar view toggle container (e.g. Lista / Kalenteri). Scrolls on narrow viewports instead of expanding the page. */
export const viewToggleContainer =
  'flex h-10 w-max max-w-full min-w-0 items-stretch overflow-x-auto overflow-y-hidden overscroll-x-contain rounded-lg border border-gray-200/80 dark:border-gray-700/80 bg-gray-100/80 dark:bg-gray-800 shadow-sm shadow-gray-200/30 dark:shadow-black/20';

/** View toggle button when active — primary surface + white label (matches navbar active). */
export const viewToggleButtonActive =
  'cursor-pointer font-semibold bg-primary text-white shadow-sm hover:bg-primary-600 dark:hover:bg-primary-600';

/** View toggle button when inactive. */
export const viewToggleButtonInactive =
  'cursor-pointer bg-gray-200/80 dark:bg-gray-700 text-muted-strong hover:text-foreground hover:bg-white/30 dark:hover:bg-gray-800/30';

/** Detail page header/content card (same shadow and border as pageCard; add padding in layout). */
export const detailPageCard =
  'bg-white dark:bg-gray-800 rounded-lg shadow-sm shadow-gray-200/60 dark:shadow-gray-900/50 border border-gray-200 dark:border-gray-700';

/** Basics block on a detail page: its own card on the canvas, same padding as the property view. */
export const detailBasicsCardClass = `${pageCard} px-4 py-4 sm:px-5 sm:py-[17px]`;

/**
 * Inner padding for list/table cards (main app lists, control panel tabs).
 * Slightly tighter section padding for denser vertical rhythm.
 */
export const listPageCardContentPadding = 'px-3 sm:px-5 py-3 sm:py-5';

/** Tighter inner padding for tabbed list pages on mobile. */
export const listPageCardContentPaddingCompact = 'px-3 sm:px-5 py-2 sm:py-3';

/** Announcements list main card — use with compact AnnouncementList on mobile. */
export const announcementsCardContentPadding = 'px-2 sm:px-6 py-3 sm:py-6';

/** Shared content width for modal wizard steppers and step bodies — fills the modal panel width. */
export const wizardContentColumnClass = 'mx-auto w-full';

/** Shared max-width for main app page content (list, detail, dashboard, footer, …). */
export const pageMaxWidthClass = 'max-w-7xl xl:max-w-[100rem] 2xl:max-w-[110rem] mx-auto w-full';

/** Detail pages stay near the design reference instead of stretching with the list canvas. */
export const detailPageMaxWidthClass = 'mx-auto w-full max-w-6xl';

/** Dashboard widgets stay readable in two columns instead of stretching with list tables. */
export const dashboardPageMaxWidthClass = 'mx-auto w-full max-w-7xl';

/** Resident, landlord, and contractor portals use the same cap so cards stay readable. */
export const portalPageMaxWidthClass = dashboardPageMaxWidthClass;

/**
 * Clears the floating assistant button on phones (48px + inset + gap).
 * Desktop keeps the original bottom padding.
 */
export const pageFabClearanceClass = 'pb-24 md:pb-24';

/** Standard main shell padding + max-width. */
export const pageMainClass = `flex-1 min-w-0 px-3 sm:px-4 pt-2 sm:pt-3 ${pageFabClearanceClass} ${pageMaxWidthClass}`;

/** Detail page shell: same padding, narrower measure. */
export const detailPageMainClass = `flex-1 min-w-0 px-3 sm:px-4 pt-2 sm:pt-3 ${pageFabClearanceClass} ${detailPageMaxWidthClass}`;

/** Main shell with slightly tighter horizontal padding. */
export const pageMainClassCompact = `flex-1 min-w-0 px-2 sm:px-4 pt-2 sm:pt-3 ${pageFabClearanceClass} ${pageMaxWidthClass}`;

/** Dashboard shell: compact padding, capped at the standard content width. */
export const dashboardPageMainClass = `flex-1 min-w-0 px-2 sm:px-4 pt-2 sm:pt-3 ${pageFabClearanceClass} ${dashboardPageMaxWidthClass}`;
